// pages/Register.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEyeSlash,
  FaUser,
  FaCamera,
  FaCheck,
  FaTimes,
  FaMapMarkerAlt,
  FaIdCard,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaVideo,
  FaImage,
  FaFolderOpen,
} from "react-icons/fa";
import api from "../../config/api";
import "./Register.css";

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    surname: "",
    email: "",
    password: "",
    password_confirmation: "",
    telephone_numbers: "",
    whatsapp_number: "",
    date_of_birth: "",
    gender: "",
    full_house_address: "",
    state_code: "",
    sd_code: "",
    fc_code: "",
    lcda_code: "",
    lg_code: "",
    ward_code: "",
    pu_code: "",
    membership_category: "",
    passport_photo: null,
  });

  const [geographicData, setGeographicData] = useState({
    states: [],
    senatorialDistricts: [],
    federalConstituencies: [],
    lcdas: [],
    localGovernments: [],
    wards: [],
    pollingUnits: [],
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [profilePreview, setProfilePreview] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [loadingGeoData, setLoadingGeoData] = useState({
    senatorialDistricts: false,
    federalConstituencies: false,
    lcdas: false,
    localGovernments: false,
    wards: false,
    pollingUnits: false,
  });

  // Camera and Upload Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("user"); // 'user' for front camera, 'environment' for back
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const formSections = [
    "Personal Info",
    "Geographic Info",
    "Contact Info",
    "Identification",
    "Account Setup",
  ];

  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 8000);
  };

  useEffect(() => {
    fetchStates();
    return () => {
      // Clean up stream when component unmounts
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Upload Modal Functions
  const openUploadModal = () => {
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
  };

  const handleFileUploadChoice = () => {
    closeUploadModal();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCameraChoice = () => {
    closeUploadModal();
    openCamera();
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      showToast(
        "Cannot access camera. Please check permissions and try again."
      );
      closeCamera();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `profile-photo-${Date.now()}.jpg`, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });

            // Set the captured photo as profile picture
            setFormData({ ...formData, passport_photo: file });

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
              setProfilePreview(e.target.result);
            };
            reader.readAsDataURL(blob);

            // Close camera modal
            closeCamera();
            showToast("Profile picture captured successfully!", "success");
          }
        },
        "image/jpeg",
        0.8
      );
    }
  };

  const switchCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    // Restart camera with new facing mode
    setTimeout(() => {
      stopCamera();
      startCamera();
    }, 100);
  };

  const openCamera = () => {
    setShowCameraModal(true);
    setTimeout(() => {
      startCamera();
    }, 100);
  };

  const closeCamera = () => {
    stopCamera();
    setShowCameraModal(false);
  };

  const fetchStates = async () => {
    try {
      const response = await api.get("/geographic/states");
      console.log("States response:", response.data);

      if (response.data.status === "success") {
        const validStates = response.data.data.filter(
          (state) =>
            state.state_code &&
            state.state_code !== "state_code" &&
            state.name !== "name" &&
            state.name &&
            state.state_code
        );
        console.log("Valid states:", validStates);
        setGeographicData((prev) => ({ ...prev, states: validStates }));
      } else {
        showToast("Failed to load states data structure");
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      showToast("Failed to load states. Please try again.");
    }
  };

  const fetchSenatorialDistricts = async (stateCode) => {
    if (!stateCode) return;

    setLoadingGeoData((prev) => ({ ...prev, senatorialDistricts: true }));

    try {
      console.log("Fetching senatorial districts for state:", stateCode);
      const response = await api.get(
        `/geographic/senatorial-districts?state_code=${stateCode}`
      );
      console.log("Senatorial districts response:", response.data);

      if (response.data.status === "success") {
        setGeographicData((prev) => ({
          ...prev,
          senatorialDistricts: response.data.data || [],
        }));
      } else {
        console.warn("No senatorial districts found for state:", stateCode);
        setGeographicData((prev) => ({
          ...prev,
          senatorialDistricts: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching senatorial districts:", error);
      setGeographicData((prev) => ({
        ...prev,
        senatorialDistricts: [],
      }));
    } finally {
      setLoadingGeoData((prev) => ({ ...prev, senatorialDistricts: false }));
    }
  };

  const fetchFederalConstituencies = async (stateCode) => {
    if (!stateCode) return;

    setLoadingGeoData((prev) => ({ ...prev, federalConstituencies: true }));

    try {
      console.log("Fetching federal constituencies for state:", stateCode);
      const response = await api.get(
        `/geographic/federal-constituencies?state_code=${stateCode}`
      );
      console.log("Federal constituencies response:", response.data);

      if (response.data.status === "success") {
        setGeographicData((prev) => ({
          ...prev,
          federalConstituencies: response.data.data || [],
        }));
      } else {
        console.warn("No federal constituencies found for state:", stateCode);
        setGeographicData((prev) => ({
          ...prev,
          federalConstituencies: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching federal constituencies:", error);
      setGeographicData((prev) => ({
        ...prev,
        federalConstituencies: [],
      }));
    } finally {
      setLoadingGeoData((prev) => ({ ...prev, federalConstituencies: false }));
    }
  };

  const fetchLCDAs = async (stateCode) => {
    if (!stateCode) return;

    setLoadingGeoData((prev) => ({ ...prev, lcdas: true }));

    try {
      console.log("Fetching LCDAs for state:", stateCode);
      const response = await api.get(
        `/geographic/lcdas?state_code=${stateCode}`
      );
      console.log("LCDAs response:", response.data);

      if (response.data.status === "success") {
        setGeographicData((prev) => ({
          ...prev,
          lcdas: response.data.data || [],
        }));
      } else {
        console.warn("No LCDAs found for state:", stateCode);
        setGeographicData((prev) => ({
          ...prev,
          lcdas: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching LCDAs:", error);
      setGeographicData((prev) => ({
        ...prev,
        lcdas: [],
      }));
    } finally {
      setLoadingGeoData((prev) => ({ ...prev, lcdas: false }));
    }
  };

  const fetchLocalGovernments = async (stateCode) => {
    if (!stateCode) return;

    setLoadingGeoData((prev) => ({ ...prev, localGovernments: true }));

    try {
      console.log("Fetching local governments for state:", stateCode);
      const response = await api.get(
        `/geographic/local-governments?state_code=${stateCode}`
      );
      console.log("Local governments response:", response.data);

      if (response.data.status === "success") {
        setGeographicData((prev) => ({
          ...prev,
          localGovernments: response.data.data || [],
        }));
      } else {
        console.warn("No local governments found for state:", stateCode);
        setGeographicData((prev) => ({
          ...prev,
          localGovernments: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching local governments:", error);
      setGeographicData((prev) => ({
        ...prev,
        localGovernments: [],
      }));
    } finally {
      setLoadingGeoData((prev) => ({ ...prev, localGovernments: false }));
    }
  };

  const fetchWards = async (lgCode) => {
    if (!lgCode) return;

    setLoadingGeoData((prev) => ({ ...prev, wards: true }));

    try {
      console.log("Fetching wards for local government:", lgCode);
      const response = await api.get(`/geographic/wards?lg_code=${lgCode}`);
      console.log("Wards response:", response.data);

      if (response.data.status === "success") {
        setGeographicData((prev) => ({
          ...prev,
          wards: response.data.data || [],
        }));
      } else {
        console.warn("No wards found for local government:", lgCode);
        setGeographicData((prev) => ({
          ...prev,
          wards: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching wards:", error);
      setGeographicData((prev) => ({
        ...prev,
        wards: [],
      }));
    } finally {
      setLoadingGeoData((prev) => ({ ...prev, wards: false }));
    }
  };

  const fetchPollingUnits = async (wardCode) => {
    if (!wardCode) return;

    setLoadingGeoData((prev) => ({ ...prev, pollingUnits: true }));

    try {
      console.log("Fetching polling units for ward:", wardCode);
      const response = await api.get(
        `/geographic/polling-units?ward_code=${wardCode}`
      );
      console.log("Polling units response:", response.data);

      if (response.data.status === "success") {
        setGeographicData((prev) => ({
          ...prev,
          pollingUnits: response.data.data || [],
        }));
      } else {
        console.warn("No polling units found for ward:", wardCode);
        setGeographicData((prev) => ({
          ...prev,
          pollingUnits: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching polling units:", error);
      setGeographicData((prev) => ({
        ...prev,
        pollingUnits: [],
      }));
    } finally {
      setLoadingGeoData((prev) => ({ ...prev, pollingUnits: false }));
    }
  };

  // Check if current state is Lagos
  const isLagosState = () => {
    if (!formData.state_code) return false;
    const selectedState = geographicData.states.find(
      (state) => state.state_code === formData.state_code
    );
    return selectedState && selectedState.name.toLowerCase().includes("lagos");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    // Reset dependent fields when parent field changes
    switch (name) {
      case "state_code":
        setFormData((prev) => ({
          ...prev,
          sd_code: "",
          fc_code: "",
          lcda_code: "",
          lg_code: "",
          ward_code: "",
          pu_code: "",
        }));
        setGeographicData((prev) => ({
          ...prev,
          senatorialDistricts: [],
          federalConstituencies: [],
          lcdas: [],
          localGovernments: [],
          wards: [],
          pollingUnits: [],
        }));
        if (value) {
          console.log("State changed to:", value);
          fetchSenatorialDistricts(value);
          fetchFederalConstituencies(value);
          fetchLocalGovernments(value);

          // Only fetch LCDAs if it's Lagos state
          const isLagos = geographicData.states
            .find((state) => state.state_code === value)
            ?.name.toLowerCase()
            .includes("lagos");

          if (isLagos) {
            fetchLCDAs(value);
          }
        }
        break;
      case "lg_code":
        setFormData((prev) => ({
          ...prev,
          ward_code: "",
          pu_code: "",
        }));
        setGeographicData((prev) => ({
          ...prev,
          wards: [],
          pollingUnits: [],
        }));
        if (value) {
          console.log("Local government changed to:", value);
          fetchWards(value);
        }
        break;
      case "ward_code":
        setFormData((prev) => ({
          ...prev,
          pu_code: "",
        }));
        setGeographicData((prev) => ({
          ...prev,
          pollingUnits: [],
        }));
        if (value) {
          console.log("Ward changed to:", value);
          fetchPollingUnits(value);
        }
        break;
      default:
        break;
    }
  };

  // Handle profile picture upload from file
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
      showToast("Profile picture uploaded successfully!", "success");
    }
  };

  // Process image file (common function for both file upload and camera capture)
  const processImageFile = (file) => {
    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      showToast("Please select a valid image file (JPEG, PNG, GIF)");
      return;
    }

    // Validate file size (max 2MB as per server validation)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      showToast("Profile picture must be less than 2MB");
      return;
    }

    setFormData({ ...formData, passport_photo: file });

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfilePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove profile picture
  const removeProfilePicture = () => {
    setFormData({ ...formData, passport_photo: null });
    setProfilePreview(null);
    showToast("Profile picture removed", "success");
  };

  // Function to extract detailed error messages from backend response
  const extractErrorMessage = (error) => {
    console.log("Registration error:", error);

    // Handle axios error response
    if (error.response?.data) {
      const data = error.response.data;

      // If there are specific field errors in data.errors
      if (data.errors && typeof data.errors === "object") {
        const errorMessages = [];

        Object.entries(data.errors).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            const fieldErrors = errors.join(", ");
            const readableField = field
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());
            errorMessages.push(`${readableField}: ${fieldErrors}`);
          }
        });

        if (errorMessages.length > 0) {
          return errorMessages.join("\n");
        }
      }

      if (data.error) {
        return data.error;
      }

      if (data.message && data.message !== "Validation failed.") {
        return data.message;
      }
    }

    if (error.message === "Network Error") {
      return "Network error. Please check your internet connection and try again.";
    }

    return "Registration failed. Please check your input and try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    // Validation
    if (formData.password !== formData.password_confirmation) {
      showToast("Passwords do not match!");
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      showToast("Password must be at least 8 characters long!");
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "telephone_numbers") {
          formDataToSend.append("telephone_numbers[]", formData[key]);
        } else if (key === "passport_photo" && formData[key]) {
          formDataToSend.append("passport_photo", formData[key]);
        } else if (key !== "passport_photo") {
          // Only send LCDA if it's selected (optional field)
          if (key === "lcda_code" && !formData[key]) {
            // Skip if LCDA is empty
            return;
          }
          formDataToSend.append(key, formData[key]);
        }
      });

      // Log the data being sent for debugging
      console.log("Submitting form data:", Object.fromEntries(formDataToSend));

      // Use the centralized API instance for registration
      const response = await api.post("/auth/register", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        showToast(
          "Registration successful! Redirecting to login...",
          "success"
        );
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        const errorMsg = extractErrorMessage({ response });
        showToast(errorMsg);
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      showToast(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Navigation functions
  const nextSection = () => {
    if (activeSection < formSections.length - 1) {
      setActiveSection(activeSection + 1);
    }
  };

  const prevSection = () => {
    if (activeSection > 0) {
      setActiveSection(activeSection - 1);
    }
  };

  // Check if dropdown should be disabled
  const isDropdownDisabled = (fieldName) => {
    switch (fieldName) {
      case "sd_code":
      case "fc_code":
      case "lg_code":
        return !formData.state_code || loadingGeoData.senatorialDistricts;
      case "lcda_code":
        return !formData.state_code || !isLagosState() || loadingGeoData.lcdas;
      case "ward_code":
        return !formData.lg_code || loadingGeoData.wards;
      case "pu_code":
        return !formData.ward_code || loadingGeoData.pollingUnits;
      default:
        return false;
    }
  };

  return (
    <div className="page-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}

      {/* Upload Choice Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="upload-modal-header">
              <h3>Choose Upload Method</h3>
              <button className="close-upload-btn" onClick={closeUploadModal}>
                <FaTimes />
              </button>
            </div>
            <div className="upload-options-modal">
              <button
                className="upload-option-card"
                onClick={handleFileUploadChoice}
              >
                <div className="upload-option-icon">
                  <FaFolderOpen />
                </div>
                <div className="upload-option-content">
                  <h4>Upload from Device</h4>
                  <p>Choose a photo from your gallery or files</p>
                </div>
              </button>
              
              <button
                className="upload-option-card"
                onClick={handleCameraChoice}
              >
                <div className="upload-option-icon">
                  <FaCamera />
                </div>
                <div className="upload-option-content">
                  <h4>Take Photo</h4>
                  <p>Use your camera to take a new photo</p>
                </div>
              </button>
            </div>
            <div className="upload-modal-footer">
              <button className="cancel-upload-btn" onClick={closeUploadModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="camera-modal-overlay">
          <div className="camera-modal">
            <div className="camera-modal-header">
              <h3>Take Profile Picture</h3>
              <button className="close-camera-btn" onClick={closeCamera}>
                <FaTimes />
              </button>
            </div>
            <div className="camera-preview">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
            <div className="camera-controls">
              <button
                className="camera-btn switch-camera-btn"
                onClick={switchCamera}
                title="Switch Camera"
              >
                <FaVideo />
              </button>
              <button className="camera-btn capture-btn" onClick={capturePhoto}>
                <FaCamera />
              </button>
              <button className="camera-btn cancel-btn" onClick={closeCamera}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="form-container">
        <div className="form-header">
          <h2 className="title">Membership Registration</h2>
          <p>
            Complete all fields to register as a verified Member.
          </p>
        </div>

        <div className="progress-steps">
          {formSections.map((section, index) => (
            <div
              key={index}
              className={`step ${index === activeSection ? "active" : ""} ${
                index < activeSection ? "completed" : ""
              }`}
              title={section}
            >
              <div className="step-number">
                {index < activeSection ? <FaCheck /> : index + 1}
              </div>
              <span className="step-label">{section}</span>
            </div>
          ))}
        </div>

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        <form onSubmit={handleSubmit} className="registration-form">
          {/* Personal Information Section */}
          {activeSection === 0 && (
            <div className="form-section">
              <div className="section-header">
                <FaUser className="section-icon" />
                <h3 className="section-title">Personal Information</h3>
              </div>

              <div className="form-grid">
                <div className="input-group">
                  <label htmlFor="first_name">First Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    placeholder="Enter your first name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="surname">Surname *</label>
                  <input
                    type="text"
                    id="surname"
                    name="surname"
                    placeholder="Enter your surname"
                    value={formData.surname}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="date_of_birth">Date of Birth *</label>
                  <input
                    type="date"
                    id="date_of_birth"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="gender">Gender *</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-next"
                  onClick={nextSection}
                >
                  Next <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          )}

          {/* Geographic Information Section */}
          {activeSection === 1 && (
            <div className="form-section">
              <div className="section-header">
                <FaMapMarkerAlt className="section-icon" />
                <h3 className="section-title">My Location</h3>
              </div>

              <div className="form-grid">
                <div className="input-group">
                  <label htmlFor="state_code">State *</label>
                  <select
                    id="state_code"
                    name="state_code"
                    value={formData.state_code}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select State</option>
                    {geographicData.states.map((state) => (
                      <option key={state.id} value={state.state_code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="sd_code">Senatorial District *</label>
                  <select
                    id="sd_code"
                    name="sd_code"
                    value={formData.sd_code}
                    onChange={handleChange}
                    required
                    disabled={isDropdownDisabled("sd_code")}
                  >
                    <option value="">Select Senatorial District</option>
                    {loadingGeoData.senatorialDistricts ? (
                      <option value="" disabled>
                        Loading...
                      </option>
                    ) : (
                      geographicData.senatorialDistricts.map((sd) => (
                        <option key={sd.id} value={sd.sd_code}>
                          {sd.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="fc_code">Federal Constituency *</label>
                  <select
                    id="fc_code"
                    name="fc_code"
                    value={formData.fc_code}
                    onChange={handleChange}
                    required
                    disabled={isDropdownDisabled("fc_code")}
                  >
                    <option value="">Select Federal Constituency</option>
                    {loadingGeoData.federalConstituencies ? (
                      <option value="" disabled>
                        Loading...
                      </option>
                    ) : (
                      geographicData.federalConstituencies.map((fc) => (
                        <option key={fc.id} value={fc.fc_code}>
                          {fc.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* LCDA Field - Only show for Lagos state */}
                {isLagosState() && (
                  <div className="input-group">
                    <label htmlFor="lcda_code">LCDA (Optional)</label>
                    <select
                      id="lcda_code"
                      name="lcda_code"
                      value={formData.lcda_code}
                      onChange={handleChange}
                      disabled={isDropdownDisabled("lcda_code")}
                    >
                      <option value="">Select LCDA (Optional)</option>
                      {loadingGeoData.lcdas ? (
                        <option value="" disabled>
                          Loading...
                        </option>
                      ) : (
                        geographicData.lcdas.map((lcda) => (
                          <option key={lcda.id} value={lcda.lcda_code}>
                            {lcda.name}
                          </option>
                        ))
                      )}
                    </select>
                    <div className="field-hint">
                      Only available for Lagos state
                    </div>
                  </div>
                )}

                <div className="input-group">
                  <label htmlFor="lg_code">Local Government *</label>
                  <select
                    id="lg_code"
                    name="lg_code"
                    value={formData.lg_code}
                    onChange={handleChange}
                    required
                    disabled={isDropdownDisabled("lg_code")}
                  >
                    <option value="">Select Local Government</option>
                    {loadingGeoData.localGovernments ? (
                      <option value="" disabled>
                        Loading...
                      </option>
                    ) : (
                      geographicData.localGovernments.map((lg) => (
                        <option key={lg.id} value={lg.lg_code}>
                          {lg.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="ward_code">Ward *</label>
                  <select
                    id="ward_code"
                    name="ward_code"
                    value={formData.ward_code}
                    onChange={handleChange}
                    required
                    disabled={isDropdownDisabled("ward_code")}
                  >
                    <option value="">Select Ward</option>
                    {loadingGeoData.wards ? (
                      <option value="" disabled>
                        Loading...
                      </option>
                    ) : (
                      geographicData.wards.map((ward) => (
                        <option key={ward.id} value={ward.ward_code}>
                          {ward.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="pu_code">Polling Unit *</label>
                  <select
                    id="pu_code"
                    name="pu_code"
                    value={formData.pu_code}
                    onChange={handleChange}
                    required
                    disabled={isDropdownDisabled("pu_code")}
                  >
                    <option value="">Select Polling Unit</option>
                    {loadingGeoData.pollingUnits ? (
                      <option value="" disabled>
                        Loading...
                      </option>
                    ) : (
                      geographicData.pollingUnits.map((pu) => (
                        <option key={pu.id} value={pu.pu_code}>
                          {pu.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-prev"
                  onClick={prevSection}
                >
                  <span className="btn-arrow">←</span> Previous
                </button>
                <button
                  type="button"
                  className="btn-next"
                  onClick={nextSection}
                >
                  Next <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          )}

          {/* Contact Information Section */}
          {activeSection === 2 && (
            <div className="form-section">
              <div className="section-header">
                <FaPhone className="section-icon" />
                <h3 className="section-title">My Contact</h3>
              </div>

              <div className="form-grid">
                <div className="input-group">
                  <label htmlFor="telephone_numbers">Phone Number *</label>
                  <input
                    type="tel"
                    id="telephone_numbers"
                    name="telephone_numbers"
                    placeholder="Enter your phone number"
                    value={formData.telephone_numbers}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="whatsapp_number">WhatsApp Number *</label>
                  <input
                    type="tel"
                    id="whatsapp_number"
                    name="whatsapp_number"
                    placeholder="Enter your WhatsApp number"
                    value={formData.whatsapp_number}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-prev"
                  onClick={prevSection}
                >
                  <span className="btn-arrow">←</span> Previous
                </button>
                <button
                  type="button"
                  className="btn-next"
                  onClick={nextSection}
                >
                  Next <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          )}

          {/* Identification Information Section */}
          {activeSection === 3 && (
            <div className="form-section">
              <div className="section-header">
                <FaIdCard className="section-icon" />
                <h3 className="section-title">My Identification</h3>
              </div>

              <div className="form-grid">
                <div className="input-group">
                  <label htmlFor="full_house_address">
                    Full House Address *
                  </label>
                  <textarea
                    id="full_house_address"
                    name="full_house_address"
                    placeholder="Enter your complete house address"
                    value={formData.full_house_address}
                    onChange={handleChange}
                    required
                    rows="3"
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="membership_category">
                    Membership Category *
                  </label>
                  <select
                    id="membership_category"
                    name="membership_category"
                    value={formData.membership_category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Volunteer">Canvasser</option>
                    <option value="Member">Non-Canvasser</option>
                  </select>
                </div>
              </div>

              {/* Profile Picture Section */}
              <div className="profile-picture-section">
                <div className="section-header">
                  <FaCamera className="section-icon" />
                  <h3 className="section-title">Profile Picture</h3>
                </div>

                <div className="profile-picture-upload">
                  <div className="profile-picture-preview">
                    {profilePreview ? (
                      <>
                        <img src={profilePreview} alt="Profile preview" />
                        <button
                          type="button"
                          className="remove-profile-picture"
                          onClick={removeProfilePicture}
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <div className="profile-picture-placeholder">
                        <FaUser />
                      </div>
                    )}
                  </div>
                  <div className="profile-upload-controls">
                    <div className="upload-options">
                      <input
                        type="file"
                        id="passport_photo"
                        name="passport_photo"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        className="profile-file-input"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="upload-option-btn upload-main-btn"
                        onClick={openUploadModal}
                      >
                        <FaCamera />
                        Upload Profile Picture
                      </button>
                    </div>
                    <p className="profile-upload-hint">
                      JPG, PNG or GIF (Max. 2MB)
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-prev"
                  onClick={prevSection}
                >
                  <span className="btn-arrow">←</span> Previous
                </button>
                <button
                  type="button"
                  className="btn-next"
                  onClick={nextSection}
                >
                  Next <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          )}

          {/* Account Setup Section */}
          {activeSection === 4 && (
            <div className="form-section">
              <div className="section-header">
                <FaLock className="section-icon" />
                <h3 className="section-title">Account Setup</h3>
              </div>

              <div className="form-grid">
                <div className="input-group">
                  <label htmlFor="password">Password *</label>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Create a secure password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength="8"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                  <div className="password-hint">
                    Use 8+ characters with a mix of letters, numbers & symbols
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="password_confirmation">
                    Confirm Password *
                  </label>
                  <div className="password-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="password_confirmation"
                      name="password_confirmation"
                      placeholder="Confirm your password"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      required
                      minLength="8"
                    />
                    <span
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="password-toggle"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-prev"
                  onClick={prevSection}
                >
                  <span className="btn-arrow">←</span> Previous
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner"></div>
                      Registering...
                    </>
                  ) : (
                    "Complete Registration"
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="form-footer">
          <p className="signin-text">
            Already have an account? <Link to="/login">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;